import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { CHANGE_SORTING } from 'src/routes/SeoApp/domain/state/sorting';
import type { StartSearchActionPayload } from '../actions/search';
import {
  CHANGE_FILTER,
  CLEAR_FILTER,
  CLEAR_FILTERS,
  SET_FILTERS,
} from '../constants/filters';
import {
  CHANGE_SEARCH_PARAMS,
  SEARCH_ENRICHED,
  SEARCH_SUCCESS,
  SEARCH_TIMED_OUT,
  START_SEARCH_PROCESS,
} from '../constants/search';
import type { GetGroupedHotelsResponse } from '../store/search/types';
import { SET_PREVIOUS_FILTERS } from '../../../store/previousFilters/constants';

export type StatusState = 'skipped' | 'performing' | 'completed' | 'no_results';

export type SearchStatusState = {
  awaitFiltration: boolean;
  hasResults: boolean;
  operatorsStatus: Record<string, StatusState>;
  hasMore: boolean;
  isDone: boolean;
  searchProgress: number;
  errorsCount: number;
  size: number | null;
  isSearchByFilters: boolean;
  firstResultsFetched: boolean;
};

export const initialState: SearchStatusState = {
  awaitFiltration: false,
  hasResults: false,
  operatorsStatus: {},
  hasMore: false,
  isDone: false,
  searchProgress: 0,
  errorsCount: 0,
  size: null,
  isSearchByFilters: false,
  firstResultsFetched: false,
};
export const name = 'searchStatus';

const changeSearchParams = (
  state: SearchStatusState,
  action: PayloadAction<StartSearchActionPayload>,
): SearchStatusState => ({
  ...state,
  hasResults: false,
  isDone: false,
  operatorsStatus: {},
  size: null,
  errorsCount: action.payload?.errorsCount || 0,
});

const searchSuccessed = (
  state: SearchStatusState,
  action: PayloadAction<GetGroupedHotelsResponse>,
): SearchStatusState => {
  const { status, hotels } = action.payload;
  const hasResults = hotels.length !== 0 && status !== null;
  return {
    ...state,
    hasMore: false,
    hasResults,
    awaitFiltration: false,
    firstResultsFetched: true,
  };
};

const searchByFilters = (state: SearchStatusState): SearchStatusState => ({
  ...state,
  awaitFiltration: true,
  isSearchByFilters: true,
});

const searchStatus = createSlice({
  name,
  initialState,
  reducers: {
    operatorsStatusChanged: (
      state: SearchStatusState,
      action: PayloadAction<
        Pick<
          SearchStatusState,
          'operatorsStatus' | 'hasMore' | 'isDone' | 'searchProgress' | 'size'
        >
      >,
    ) => ({
      ...state,
      ...action.payload,
    }),
    resetIsSearchByFilters: (state: SearchStatusState) => {
      state.isSearchByFilters = false;
    },
    setHasMoreResults: (
      state: SearchStatusState,
      action: PayloadAction<SearchStatusState['hasMore']>,
    ) => {
      state.hasMore = action.payload;
    },
  },
  extraReducers: {
    [START_SEARCH_PROCESS]: changeSearchParams,
    [CHANGE_SEARCH_PARAMS]: changeSearchParams,
    [SEARCH_ENRICHED]: searchSuccessed,
    [SEARCH_SUCCESS]: searchSuccessed,
    [CLEAR_FILTER]: searchByFilters,
    [CLEAR_FILTERS]: searchByFilters,
    [CHANGE_FILTER]: searchByFilters,
    [CHANGE_SORTING]: searchByFilters,
    [SET_PREVIOUS_FILTERS]: searchByFilters,
    [SET_FILTERS]: searchByFilters,
    [SEARCH_TIMED_OUT]: (state: SearchStatusState) => {
      state.isDone = true;
    },
  },
});

export const {
  operatorsStatusChanged,
  resetIsSearchByFilters,
  setHasMoreResults,
} = searchStatus.actions;

export default searchStatus.reducer;
